// Monitor do quarto — a tela que fica ligada ao lado da cama.
//
// Leitura em primeiro lugar: de longe, só se lê. De perto, tocar num sinal
// abre as ações dele — lançar o valor à mão quando o aparelho falhou, ou ver
// as últimas 24 h. Nenhuma ação destrutiva mora aqui, então toque por engano
// não estraga nada: abre uma folha e se fecha (docs/PRODUCT.md §6.1).
//
// Redesenhada em 19/09 a partir de uma tela que o dono montou. O motivo foi
// claro: "pessoas que têm uma visão sem um óculos não conseguem visualizar
// direito". Então tudo aqui é maior, o que se usa todo dia fica no alto ou na
// barra de baixo, e o resto vai para "Mais" — que não é esconder, é tirar da
// frente o que não se procura às três da manhã.
import { useEffect, useState, type ReactElement } from 'react';
import type { LeituraDTO, ResumoRoteiroDTO, TipoLeitura, UsuarioDTO } from '@medicov1/shared';
import { podeEscrever, formatarHora, idadeEm, avisosDeSeguranca } from '@medicov1/shared';
import { Coletor, type StatusAparelho } from '../ble/coletor';
import type { Paciente } from '../App';
import { estadoDaLeitura, idadeEmTexto, type Estado } from './estadoDoDado';
import { equipeEmTexto, type PlantaoDTO } from './Plantao';
import { LancarSinal } from './LancarSinal';
import './monitor.css';

/**
 * Cada sinal tem um desenho.
 *
 * Pedido do dono para a frequência cardíaca — "coloca um símbolozinho do
 * coração, que é para a pessoa ver que aquilo ali é o batimento cardíaco" — e
 * estendido aos cinco, porque a razão vale para todos e um só com desenho
 * ficaria parecendo erro. Quem entra no quarto e nunca viu a tela reconhece a
 * figura antes de ler a sigla; "SpO₂" não diz nada a quase ninguém.
 */
const DESENHO = {
  coracao: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20s-7-4.6-7-9.4A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.6C19 15.4 12 20 12 20z" />
    </svg>
  ),
  pulmao: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v9" />
      <path d="M12 8c-1.6 0-3 .9-3.6 2.4L6 16.3c-.6 1.6.5 3.2 2.1 3.2 1.4 0 2.6-1 2.8-2.4l1.1-6.6" />
      <path d="M12 8c1.6 0 3 .9 3.6 2.4l2.4 5.9c.6 1.6-.5 3.2-2.1 3.2-1.4 0-2.6-1-2.8-2.4l-1.1-6.6" />
    </svg>
  ),
  medidor: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 18a8 8 0 1 1 16 0" />
      <path d="M12 18l4-5" />
    </svg>
  ),
  gota: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.5s5.5 6 5.5 9.6a5.5 5.5 0 1 1-11 0C6.5 9.5 12 3.5 12 3.5z" />
    </svg>
  ),
  termometro: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 14.8V5a2 2 0 1 0-4 0v9.8a4 4 0 1 0 4 0z" />
      <path d="M12 9v6" />
    </svg>
  ),
};

const EIXOS: {
  tipo: TipoLeitura; rotulo: string; unidade: string; cor: string; desenho: ReactElement;
}[] = [
  { tipo: 'frequencia_cardiaca', rotulo: 'FC', unidade: 'bpm', cor: 'var(--m-fc)', desenho: DESENHO.coracao },
  { tipo: 'saturacao', rotulo: 'SpO₂', unidade: '%', cor: 'var(--m-spo2)', desenho: DESENHO.pulmao },
  { tipo: 'pa_sistolica', rotulo: 'Pressão', unidade: 'mmHg', cor: 'var(--m-pa)', desenho: DESENHO.medidor },
  { tipo: 'glicemia', rotulo: 'Glicemia', unidade: 'mg/dL', cor: 'var(--m-glicemia)', desenho: DESENHO.gota },
  { tipo: 'temperatura', rotulo: 'Temperatura', unidade: '°C', cor: 'var(--m-temperatura)', desenho: DESENHO.termometro },
];

/**
 * Escuro por padrão: o quarto tem alguém dormindo nele, e a tela fica ligada a
 * noite inteira. Quem preferir claro troca em "Mais", e a escolha fica guardada
 * naquele aparelho — não se impõe tema por horário, porque quem sabe se a luz
 * do quarto está acesa é quem está lá.
 */
const TEMA_PADRAO = 'escuro' as const;

const ROTULO_TURNO: Record<PlantaoDTO['turno'], string> = {
  manha: 'Plantão da manhã', tarde: 'Plantão da tarde', noite: 'Plantão da noite',
};

/** De onde veio a medida, em uma palavra — é o que o selo do cartão mostra. */
const ROTULO_ORIGEM_CURTO: Record<string, string> = {
  manual: 'Manual', ble: 'Bluetooth', sensor_continuo: 'Sensor', exame: 'Exame',
};

export interface ResumoRemedios {
  total: number;
  dadas: number;
  atrasadas: number;
  proxima: { nome: string; hora: string } | null;
  /** Sinalização urgente (28/09): remédio que pede confirmação antes de dar. */
  alertas?: { medicacaoId: string; nome: string; alerta: string }[];
}

interface Props {
  usuario: UsuarioDTO;
  paciente: Paciente | null;
  leituras: LeituraDTO[];
  erro: string;
  status: StatusAparelho[];
  naFila: number;
  aoAbrirAparelhos: () => void;
  aoAbrirRegistrar: () => void;
  aoAbrirTempo: () => void;
  aoAbrirAnotar: () => void;
  aoAbrirFicha: () => void;
  aoAbrirPlantao: () => void;
  aoAbrirImportar: () => void;
  aoAbrirRelatorio: () => void;
  aoAbrirEvolucao: () => void;
  /** Ninguem registrou como ela esta hoje — o monitor lembra enquanto falta. */
  faltaObservacaoDeHoje: boolean;
  /** O roteiro de perguntas da próxima visita, se houver. */
  roteiroAtivo: ResumoRoteiroDTO | null;
  /** Remédios de hoje: quantos dados, quantos sem confirmação, o próximo. */
  remedios: ResumoRemedios | null;
  aoAbrirRemedios: () => void;
  aoAbrirPerguntas: () => void;
  aoConferirMedicacao: () => void;
  /** Um sinal foi lançado pelo toque no cartão: o monitor busca de novo. */
  aoGravarLeitura: () => void;
  plantao: PlantaoDTO | null;
  aoSair: () => void;
}

// ----------------------------------------------------------------- ícones
// Desenhados aqui para não depender de biblioteca: são oito traços, e a tela
// precisa abrir no 4G do hospital.
const Icone = {
  pulso: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h4l2-6 4 12 2-6h6" /></svg>
  ),
  lua: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" /></svg>
  ),
  sol: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </svg>
  ),
  comprimido: () => (
    // Cápsula deitada na diagonal, com o divisor ATRAVESSADO — no eixo longo
    // ele vira um clipe de papel.
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)" />
      <path d="M9.2 9.2l5.6 5.6" />
    </svg>
  ),
  caneta: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" />
    </svg>
  ),
  relogio: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ),
  alerta: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
  ),
  casa: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10" /></svg>
  ),
  barras: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V10M12 20V4M19 20v-7" /></svg>
  ),
  lista: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
  ),
  pessoas: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" /><circle cx="17" cy="9" r="2.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M15.5 20a5 5 0 0 1 6-4.6" />
    </svg>
  ),
  mais: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
  ),
  documento: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h8l5 5v13H6zM14 3v5h5M9 13h6M9 17h6" /></svg>
  ),
  importar: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v11M8 10l4 4 4-4M4 17v3h16v-3" /></svg>
  ),
  antena: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="2" />
      <path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7M5.7 18.3a9 9 0 0 1 0-12.6M18.3 5.7a9 9 0 0 1 0 12.6" />
    </svg>
  ),
  sair: () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4h4v16h-4M11 8l-4 4 4 4M7 12h10" /></svg>
  ),
};

export function Monitor({
  usuario, paciente, leituras, erro, status, naFila,
  aoAbrirAparelhos, aoAbrirRegistrar, aoAbrirTempo, aoAbrirAnotar, aoAbrirFicha,
  aoAbrirPlantao, aoAbrirImportar, aoAbrirRelatorio, aoAbrirEvolucao, faltaObservacaoDeHoje,
  roteiroAtivo, aoAbrirPerguntas, remedios, aoAbrirRemedios, aoConferirMedicacao, aoGravarLeitura, plantao, aoSair,
}: Props) {
  // Qual sinal teve o cartão tocado. Null = ninguém tocou, tela limpa.
  const [tocado, setTocado] = useState<(typeof EIXOS)[number] | null>(null);
  // A folha "Mais": o que não se usa todo dia, fora da frente.
  const [maisAberto, setMaisAberto] = useState(false);
  // "FC 90 bpm gravado": a confirmação fica uns segundos embaixo dos cartões.
  const [gravado, setGravado] = useState('');
  useEffect(() => {
    if (!gravado) return;
    const t = setTimeout(() => setGravado(''), 4000);
    return () => clearTimeout(t);
  }, [gravado]);
  // Quem só acompanha vê tudo, mas não lança nada. Os botões de escrever somem
  // em vez de virarem erro ao toque.
  const registra = podeEscrever(usuario.perfil);
  const [relogio, setRelogio] = useState(new Date());
  const [tema, setTema] = useState<'claro' | 'escuro'>(() => {
    try {
      return (localStorage.getItem('medicov1:tema') as 'claro' | 'escuro' | null) ?? TEMA_PADRAO;
    } catch {
      return TEMA_PADRAO; // aba privada ou site data bloqueado: segue no padrão
    }
  });

  // O relógio anda de 10 em 10 s e também redesenha as idades das medidas —
  // "há 2 min" precisa virar "há 3 min" sozinho.
  useEffect(() => {
    const t = setInterval(() => setRelogio(new Date()), 10_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('medicov1:tema', tema); } catch { /* sem storage: vale só nesta sessão */ }
  }, [tema]);

  // Avisos de segurança já levados à enfermagem, pela leitura que disparou:
  // a próxima medida fora da faixa avisa de novo. Vale só neste aparelho.
  const [avisosVistos, setAvisosVistos] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('medicov1:avisosVistos') ?? '[]') as string[]; } catch { return []; }
  });
  const marcarAvisoVisto = (leituraId: string) => {
    const lista = [...avisosVistos, leituraId].slice(-50);
    setAvisosVistos(lista);
    try { localStorage.setItem('medicov1:avisosVistos', JSON.stringify(lista)); } catch { /* sem storage: vale só nesta sessão */ }
  };

  // Mantém a tela acesa: Chrome no Android derruba conexão BLE com a tela
  // apagada, e um monitor que apaga sozinho não é monitor.
  useEffect(() => {
    let travo: WakeLockSentinel | null = null;
    const pedir = async () => {
      try { travo = await navigator.wakeLock?.request('screen'); } catch { /* sem wake lock: segue */ }
    };
    void pedir();
    const aoVoltar = () => { if (document.visibilityState === 'visible') void pedir(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { document.removeEventListener('visibilitychange', aoVoltar); void travo?.release(); };
  }, []);

  const porTipo = new Map(leituras.map((l) => [l.tipo, l]));
  const diastolica = porTipo.get('pa_diastolica');
  const agora = relogio.getTime();
  const avisos = avisosDeSeguranca(leituras, agora).filter((a) => !avisosVistos.includes(a.leituraId));
  const conectados =status.filter((a) => a.estado === 'conectado').length;
  /**
   * O item "Aparelhos" precisa aparecer ANTES de haver aparelho.
   *
   * Ele só surgia quando já havia um conectado ou medida na fila — e não há
   * como conectar o primeiro sem entrar na tela. Galinha e ovo: o dono comprou
   * uma cinta cardíaca, perguntou se o sistema lia, e não tinha por onde nem
   * tentar. Agora basta o navegador ter Bluetooth (Chrome no Android ou no
   * computador; no iPhone nenhum navegador da Apple tem, e só um como o Bluefy
   * resolve). Sem suporte no navegador, o item some de novo: prometer
   * pareamento que não acontece é pior que não oferecer. */
  const temBluetooth = Coletor.suportado();
  const IconeTurno = plantao?.turno === 'noite' ? Icone.lua : Icone.sol;

  /**
   * O selo do canto do cartão: de onde veio a medida, ou que ela envelheceu.
   *
   * O aviso "Atualizar · há 8 h 54 min" não cabe num cartão de meia tela — ele
   * saía cortado no celular do dono. Então o selo âmbar só aparece onde há
   * largura para ele; no celular, quem avisa é o rodapé do cartão, que fica
   * âmbar e traz a mesma idade. O aviso não se perde, só muda de lugar.
   */
  const selo = (leitura: LeituraDTO | undefined, estado: Estado) => {
    if (estado === 'ausente' || !leitura) return <span className="sinal__selo sinal__selo--vazio">Sem leitura</span>;
    if (estado === 'antiga') {
      // O âmbar chama para uma ação, não só avisa: "Atualizar" é o que se faz.
      return (
        <>
          <span className="sinal__selo sinal__selo--atualizar">
            <Icone.alerta /> Atualizar · {idadeEmTexto(leitura.medidoEm, agora)}
          </span>
          <span className="sinal__selo sinal__selo--origem">
            {ROTULO_ORIGEM_CURTO[leitura.origem] ?? leitura.origem}
          </span>
        </>
      );
    }
    if (leitura.ehReferencia) return <span className="sinal__selo sinal__selo--teste">Teste</span>;
    if (estado === 'ao_vivo') return <span className="sinal__selo sinal__selo--vivo"><i /> Ao vivo</span>;
    // De onde veio a medida some no celular: é "Manual" repetido nos quatro
    // cartões, e o desenho do sinal precisa do espaço. Quem quiser saber toca
    // no cartão ou abre as 24 h, onde a origem vem por extenso.
    return (
      <span className="sinal__selo sinal__selo--origem">
        {ROTULO_ORIGEM_CURTO[leitura.origem] ?? leitura.origem}
      </span>
    );
  };

  /**
   * A idade em uma palavra: "há 1 h", não "há 1 h 2 min". A linha de baixo do
   * cartão tem de caber numa linha só — e os minutos exatos de uma medida de
   * uma hora atrás não mudam decisão nenhuma. O selo âmbar, esse traz os
   * minutos, porque ali o tempo é o motivo do aviso.
   */
  const idadeCurta = (iso: string) => {
    const minutos = Math.floor((agora - new Date(iso).getTime()) / 60_000);
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `há ${horas} h`;
    return `há ${Math.floor(horas / 24)} d`;
  };

  /**
   * A linha de baixo do cartão: quando foi medido.
   *
   * Traz a idade em TODOS os estados, inclusive no antigo — porque no celular
   * esta linha é o único lugar que avisa que a medida envelheceu, já que o selo
   * âmbar não cabe ali. A cor do rodapé é que dá o alarme.
   */
  const quando = (leitura: LeituraDTO | undefined, estado: Estado) => {
    if (!leitura || estado === 'ausente') return 'nenhuma leitura registrada';
    if (estado === 'ao_vivo') return `atualizado ${idadeCurta(leitura.recebidoEm)}`;
    return `${formatarHora(leitura.medidoEm)} · ${idadeCurta(leitura.medidoEm)}`;
  };

  const cartao = (eixo: (typeof EIXOS)[number], largo = false) => {
    const leitura = porTipo.get(eixo.tipo);
    const estado = estadoDaLeitura(leitura, agora);
    const ehPa = eixo.tipo === 'pa_sistolica';
    return (
      <section
        key={eixo.tipo}
        className={`sinal${largo ? ' sinal--largo' : ''}`}
        data-estado={estado}
        style={{ ['--cor' as string]: eixo.cor }}
        // O cartão inteiro é o alvo do toque: no celular, mirar um botão
        // pequeno com o aparelho na outra mão não funciona.
        role="button"
        tabIndex={0}
        onClick={() => setTocado(eixo)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTocado(eixo); }}
        aria-label={`${eixo.rotulo}: abrir opções`}
      >
        <div className="sinal__topo">
          <b className="sinal__rotulo">
            <span className="sinal__desenho">{eixo.desenho}</span>
            {eixo.rotulo}
          </b>
          {selo(leitura, estado)}
        </div>
        <div className="sinal__corpo">
        <div className="sinal__valor">
          {leitura ? (
            <>
              <span className="sinal__numero">
                {Number(leitura.valor).toLocaleString('pt-BR')}
                {ehPa && diastolica && (
                  <>
                    <u>/</u>
                    <s>{Number(diastolica.valor).toLocaleString('pt-BR')}</s>
                  </>
                )}
              </span>
              <span className="sinal__unidade">{eixo.unidade}</span>
            </>
          ) : (
            <span className="sinal__numero sinal__numero--vazio">—</span>
          )}
        </div>
        <div className="sinal__quando">
          {estado === 'antiga' ? <Icone.alerta /> : <Icone.relogio />} {quando(leitura, estado)}
        </div>
        </div>
      </section>
    );
  };

  const fecharMais = (acao: () => void) => () => { setMaisAberto(false); acao(); };

  return (
    <div className="monitor" data-tema={tema}>
      <div className="mon__conteudo">
        {/* ------------------------------------------------------------ topo */}
        <header className="mon__topo">
          <span className="mon__icone"><Icone.pulso /></span>
          <div className="mon__titulos">
            {/* Só "Monitor": o nome dela está no cartão de plantão, na ficha e
                em todo relatório — aqui só tomava a linha do relógio. */}
            <h1>Monitor</h1>
            {paciente && (
              <p>
                {idadeEm(paciente.anoNascimento)} anos
                {/* O quarto vem cedo: é a primeira coisa que alguém precisa
                    saber ao chegar no hospital. */}
                {paciente.quarto && <> · Quarto {paciente.quarto}</>}
                {' · '}{paciente.diasInternada}º dia
              </p>
            )}
          </div>
          <time className="mon__relogio">{formatarHora(relogio)}</time>
        </header>

        {erro && <p className="aviso-erro mon__erro">{erro}</p>}
        {naFila > 0 && (
          <p className="mon__fila">
            <Icone.alerta /> {naFila} {naFila === 1 ? 'medida guardada' : 'medidas guardadas'} para enviar quando a internet voltar
          </p>
        )}

        {/* ---------------------------------------------------- sinais vitais */}
        <div className="mon__secao">
          <h2>Sinais vitais</h2>
          <span>Últimas medições</span>
        </div>

        <div className="mon__grade">
          {EIXOS.slice(0, 4).map((eixo) => cartao(eixo))}
        </div>
        {cartao(EIXOS[4]!, true)}
        {gravado && <p className="mon__gravado" role="status">✓ {gravado}</p>}

        {/* Faixas de segurança (pedido do Ilton, 30/09): a última medida de um
            sinal saiu da faixa — o que conferir e a quem levar o número. O
            médico passa uma vez por dia; quem está sempre ali é a enfermagem. */}
        {avisos.map((a) => (
          <div key={a.chave} className={`mon__urgente mon__seguranca mon__seguranca--${a.nivel}`} role="alert">
            <Icone.alerta />
            <span>
              <em>{a.nivel === 'agora' ? 'Chame a enfermagem agora' : 'Leve o número à enfermagem'} · {formatarHora(a.medidoEm)}</em>
              <b>{a.titulo}</b>
              <small>{a.oQueFazer}</small>
              <small className="mon__seguranca__fonte">{a.fonte}</small>
              <button type="button" className="mon__seguranca__visto" onClick={() => marcarAvisoVisto(a.leituraId)}>
                Já levei à enfermagem
              </button>
            </span>
          </div>
        ))}

        {/* Sinalização urgente (pedido do Ilton, 28/09): logo abaixo dos sinais,
            em vermelho, enquanto houver remédio que pede confirmação antes de
            dar. Toque leva à lista, onde se resolve. */}
        {remedios?.alertas && remedios.alertas.length > 0 && (
          <button className="mon__urgente" onClick={aoAbrirRemedios} role="alert">
            <Icone.alerta />
            <span>
              <b>Sinalização urgente · {remedios.alertas.length === 1 ? remedios.alertas[0]!.nome : `${remedios.alertas.length} remédios`}</b>
              <small>{remedios.alertas.length === 1 ? remedios.alertas[0]!.alerta : remedios.alertas.map((a) => a.nome).join(', ')}</small>
            </span>
          </button>
        )}

        {/* Abaixo dos sinais, o resto (pedido do Ilton, 27/09): os números ficam
            sempre no alto da tela, e o que se acrescenta aqui não os empurra
            para baixo. Logo abaixo deles, quem está de serviço: com quem falar. */}
        <button className="mon__plantao" onClick={aoAbrirPlantao} disabled={!registra && !plantao}>
          <span className={`mon__plantao__icone${plantao ? '' : ' mon__plantao__icone--vazio'}`}>
            <IconeTurno />
          </span>
          <span className="mon__plantao__texto">
            {plantao ? (
              <>
                <b>{ROTULO_TURNO[plantao.turno]}</b>
                <span>{equipeEmTexto(plantao) || 'sem nomes registrados'}</span>
              </>
            ) : (
              <>
                <b>Sem plantão registrado</b>
                <span>{registra ? 'toque para registrar quem está de serviço' : 'ninguém registrou o turno'}</span>
              </>
            )}
          </span>
          {plantao && (
            <span className="mon__plantao__desde">
              <small>desde</small>
              <b>{formatarHora(plantao.inicioEm)}</b>
            </span>
          )}
        </button>

        {/* As duas ações de todo dia. Conferir vem em destaque e maior: é o
            único botão que se usa com a enfermeira esperando do lado, e
            procurar não é opção. */}
        <div className="mon__acoes">
          <button className="mon__acao mon__acao--principal" onClick={aoConferirMedicacao}>
            <Icone.comprimido /> Conferir medicação
          </button>
          {registra && (
            <button className="mon__acao" onClick={aoAbrirAnotar}>
              <Icone.caneta /> Anotar
            </button>
          )}
        </div>

        {/* Remédios de hoje (27/09): o que a prescrição prevê e o que já foi
            marcado. Sem confirmação passada a hora, o card fica âmbar e
            pergunta — "foi dado?" não é acusação, é o que falta registrar. */}
        {remedios && remedios.total > 0 && (
          <button className={`mon__lembrete mon__lembrete--remedios${remedios.atrasadas > 0 ? ' mon__lembrete--atrasado' : ''}`} onClick={aoAbrirRemedios}>
            <Icone.comprimido />
            <span>
              <b>Remédios de hoje · {remedios.dadas} de {remedios.total}</b>
              <small>
                {remedios.atrasadas > 0
                  ? `${remedios.atrasadas} sem confirmação — foi dado? toque para marcar`
                  : remedios.proxima ? `próximo: ${remedios.proxima.nome} às ${remedios.proxima.hora}` : 'todos os de hoje marcados'}
              </small>
            </span>
          </button>
        )}

        {/* Perguntas da próxima visita (27/09): aparece enquanto houver um
            roteiro ativo — é o que se abre com a médica chegando no quarto. */}
        {roteiroAtivo && (
          <button className="mon__lembrete mon__lembrete--perguntas" onClick={aoAbrirPerguntas}>
            <Icone.lista />
            <span>
              <b>Perguntas da visita</b>
              <small>
                {roteiroAtivo.marcadas > 0
                  ? `${roteiroAtivo.marcadas} de ${roteiroAtivo.perguntas} respondidas — toque para continuar`
                  : `${roteiroAtivo.perguntas} perguntas sobre os exames — toque na hora da visita`}
              </small>
            </span>
          </button>
        )}

        {/* Lembrete da observacao do dia. So aparece para quem registra e so
            enquanto falta: o valor dela esta em ser feita TODO dia, e a unica
            forma de isso acontecer e o monitor perguntar. */}
        {registra && faltaObservacaoDeHoje && (
          <button className="mon__lembrete" onClick={aoAbrirEvolucao}>
            <Icone.caneta />
            <span>
              <b>Como ela está hoje?</b>
              <small>três perguntas, vinte segundos — é a sequência que mostra a evolução</small>
            </span>
          </button>
        )}


        {/* A legenda existe para quem chega no quarto sem nunca ter visto a
            tela. Fechada por padrão: quem já sabe não precisa dela ocupando
            espaço dos números. */}
        <details className="mon__legenda">
          <summary><Icone.documento /> Legenda das medições <i /></summary>
          <ul>
            <li><b className="sinal__selo sinal__selo--vivo"><i /> Ao vivo</b> chegando sozinho agora, de um aparelho ligado</li>
            <li><b className="sinal__selo">Manual</b> alguém mediu e digitou, na hora indicada</li>
            <li><b className="sinal__selo">Bluetooth</b> veio de um aparelho pareado, na hora indicada</li>
            <li><b className="sinal__selo sinal__selo--atualizar"><Icone.alerta /> Atualizar</b> a última medida tem mais de 2 horas</li>
            <li><b className="sinal__selo sinal__selo--teste">Teste</b> medida de teste do sistema, não é dela</li>
            <li>Toque em qualquer cartão para lançar um valor à mão ou ver as últimas 24 h.</li>
          </ul>
        </details>

        <p className="mon__rodape">Monitor de apoio · não é dispositivo médico</p>
      </div>

      {/* ------------------------------------------------------ barra de baixo
          O que se abre todo dia, à distância de um polegar. O resto vai para
          "Mais" — não é esconder, é tirar da frente. */}
      <nav className="mon__nav" aria-label="Seções">
        <button className="mon__nav__item mon__nav__item--ativo" aria-current="page">
          <Icone.casa /><span>Início</span>
        </button>
        <button className="mon__nav__item" onClick={aoAbrirTempo}>
          <Icone.barras /><span>24 h</span>
        </button>
        {/* "Medidas" saiu daqui e foi para "Mais": tocar no número do sinal já
            leva direto para lançar aquele valor, com o campo no foco. Um atalho
            que ninguém usa porque existe um caminho melhor só ocupa o polegar. */}
        <button className="mon__nav__item" onClick={aoAbrirFicha}>
          <Icone.lista /><span>Ficha</span>
        </button>
        <button className="mon__nav__item" onClick={aoAbrirPlantao}>
          <Icone.pessoas /><span>Plantão</span>
        </button>
        <button className="mon__nav__item" onClick={() => setMaisAberto(true)}>
          <Icone.mais /><span>Mais</span>
          {(conectados > 0 || naFila > 0) && <i className="mon__nav__ponto" />}
        </button>
      </nav>

      {/* Folha "Mais": o que não se usa todo dia. */}
      {maisAberto && (
        <div className="folha" onClick={() => setMaisAberto(false)} role="presentation">
          <div className="folha__caixa" onClick={(e) => e.stopPropagation()}>
            <div className="folha__topo"><b>Mais</b><small>{usuario.nome}</small></div>

            {/* Cada item é um botão com cara de botão: ícone à esquerda, seta à
                direita, fundo preenchido. Numa lista de texto com borda fina,
                no celular, não fica claro o que se aperta. */}
            {registra && (
              <button className="folha__acao" onClick={fecharMais(aoAbrirRegistrar)}>
                <i className="folha__icone"><Icone.lista /></i>
                <span>Medidas</span>
                <small>lançar pressão, glicemia, temperatura e o resto à mão</small>
              </button>
            )}
            {/* Relatório é para TODO MUNDO: quem só acompanha também precisa
                mostrar ao médico, e ler não estraga nada. */}
            <button className="folha__acao" onClick={fecharMais(aoAbrirEvolucao)}>
              <i className="folha__icone"><Icone.barras /></i>
              <span>Como ela está</span>
              <small>as três perguntas do dia e a linha dos últimos dias</small>
            </button>
            <button className="folha__acao" onClick={fecharMais(aoAbrirRemedios)}>
              <i className="folha__icone"><Icone.comprimido /></i>
              <span>Remédios de hoje</span>
              <small>as doses da prescrição, e marcar o que foi dado</small>
            </button>
            <button className="folha__acao" onClick={fecharMais(aoAbrirPerguntas)}>
              <i className="folha__icone"><Icone.lista /></i>
              <span>Perguntas da visita</span>
              <small>os roteiros de perguntas sobre os exames, e as respostas gravadas</small>
            </button>
            <button className="folha__acao" onClick={fecharMais(aoAbrirRelatorio)}>
              <i className="folha__icone"><Icone.documento /></i>
              <span>Relatório</span>
              <small>passar o turno, diário, semanal — e o PDF para o médico</small>
            </button>
            {registra && (
              <button className="folha__acao" onClick={fecharMais(aoAbrirImportar)}>
                <i className="folha__icone"><Icone.importar /></i>
                <span>Importar</span>
                <small>exames em foto ou PDF, lidos e lançados</small>
              </button>
            )}
            {/* Bluetooth saiu do menu por decisão do dono em 17/09: as medidas
                passam a ser digitadas. O item continua discreto — último da
                lista — mas aparece sempre que o navegador souber falar
                Bluetooth, senão não há como parear o primeiro aparelho. */}
            {(temBluetooth || conectados > 0 || naFila > 0) && (
              <button className="folha__acao" onClick={fecharMais(aoAbrirAparelhos)}>
                <i className="folha__icone"><Icone.antena /></i>
                <span>
                  Aparelhos
                  {conectados > 0 && <b className="mon__conta">{conectados}</b>}
                  {naFila > 0 && <b className="mon__conta mon__conta--fila">{naFila}</b>}
                </span>
                <small>
                  {conectados > 0 || naFila > 0
                    ? 'o que está pareado e o que está na fila para enviar'
                    : 'parear uma cinta cardíaca, o oxímetro ou a pressão'}
                </small>
              </button>
            )}
            <button className="folha__acao" onClick={() => setTema(tema === 'claro' ? 'escuro' : 'claro')}>
              <i className="folha__icone">{tema === 'claro' ? <Icone.lua /> : <Icone.sol />}</i>
              <span>{tema === 'claro' ? 'Modo noite' : 'Modo dia'}</span>
              <small>{tema === 'claro' ? 'tela escura, para não incomodar quem dorme' : 'tela clara, para o dia com luz na janela'}</small>
            </button>
            <button className="folha__acao" onClick={fecharMais(aoSair)}>
              <i className="folha__icone"><Icone.sair /></i>
              <span>Sair</span>
              <small>encerra a sessão de {usuario.nome} neste aparelho</small>
            </button>

            <button className="folha__acao folha__acao--fechar" onClick={() => setMaisAberto(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Toque no cartão: só aquele sinal, para lançar e voltar (27/09). A
          tela com todas as medidas continua em Mais → Medidas. */}
      {tocado && (
        <LancarSinal
          eixo={tocado}
          pacienteId={paciente?.id ?? null}
          ultimo={porTipo.get(tocado.tipo)}
          ultimaDiastolica={tocado.tipo === 'pa_sistolica' ? diastolica : undefined}
          registra={registra}
          aoGravar={(resumo) => { setTocado(null); setGravado(resumo); aoGravarLeitura(); }}
          aoVer24h={() => { setTocado(null); aoAbrirTempo(); }}
          aoFechar={() => setTocado(null)}
        />
      )}
    </div>
  );
}
